import { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/i18n/index";
import PatientLayout from "@/components/layout/PatientLayout";
import api from "@/api";
import { getItems } from "@/utils/apiHelpers";
import { FileText, Download, Upload, X, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";

const DOC_TYPE_COLORS = {
  ordonnance_medicament: "bg-emerald-100 text-emerald-700",
  ordonnance_analyses: "bg-green-100 text-green-700",
  ordonnance_imagerie: "bg-orange-100 text-orange-700",
  resultat_analyses: "bg-cyan-100 text-cyan-700",
  resultat_imagerie: "bg-indigo-100 text-indigo-700",
  compte_rendu: "bg-purple-100 text-purple-700",
  consentement: "bg-blue-100 text-blue-700",
  photo_patient: "bg-sky-100 text-sky-700",
  autre: "bg-slate-100 text-slate-700",
  // Legacy
  prescription: "bg-emerald-100 text-emerald-700",
  lab_result: "bg-green-100 text-green-700",
  imaging: "bg-orange-100 text-orange-700",
  medical_report: "bg-purple-100 text-purple-700",
  consent_form: "bg-blue-100 text-blue-700",
  other: "bg-slate-100 text-slate-700",
  lab_order: "bg-green-100 text-green-700",
  imaging_order: "bg-orange-100 text-orange-700",
};

function getDocumentSourceLabel(doc, t) {
  const raw = String(doc.uploadedByRole || doc.source || doc.uploadedBy || "").toUpperCase();
  if (raw.includes("PATIENT")) return t("documents.sourcePatient");
  if (raw.includes("LAB") || raw.includes("LABORATOIRE")) return t("documents.sourceLab");
  if (raw.includes("IMAGERIE")) return t("documents.sourceImaging");
  if (raw.includes("PHARM") || raw.includes("MEDECIN") || raw.includes("IDE") || raw.includes("NURSE") || raw.includes("DOCTOR")) return t("documents.sourceTeam");
  return t("documents.sourceAvailable");
}

function getDocumentStatusLabel(doc, t) {
  const raw = String(doc.status || doc.reviewStatus || "").toUpperCase();
  if (["PENDING", "PENDING_REVIEW", "RECEIVED", "UPLOADED"].includes(raw)) return t("documents.statusReceived");
  if (["VALIDATED", "REVIEWED", "CLASSIFIED", "COMPLETED"].includes(raw)) return t("documents.statusClassified");
  return t("documents.statusAvailable");
}

export default function PatientMedicalRecord() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const location = useLocation();
  const [docs, setDocs] = useState([]);
  const [filter, setFilter] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedType, setSelectedType] = useState("");
  const [docTypes, setDocTypes] = useState([]);
  const fileInputRef = useRef(null);

  const fetchDocs = () => {
    if (!user?.id) return;
    api.get(`/patients/${user.id}/documents`, { skipAuthRedirect: true })
      .then(res => setDocs(getItems(res.data)))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchDocs();
    api.get("/documents/types", { skipAuthRedirect: true })
      .then(res => setDocTypes(getItems(res.data)))
      .catch(() => setDocTypes([]));
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("upload") === "1") {
      setShowUpload(true);
    }
  }, [location.search]);

  useEffect(() => {
    fetchDocs();
  }, [user.id]);

  const safeDocs = Array.isArray(docs) ? docs : [];
  const safeDocTypes = Array.isArray(docTypes) ? docTypes : [];
  const filtered = filter === "ALL" ? safeDocs : safeDocs.filter(d => d.type === filter);
  const unsureDocType = safeDocTypes.find(dt => ["autre", "other"].includes(dt.value));
  const uploadDocTypes = safeDocTypes.filter(dt => !["resultat_analyses", "lab_result"].includes(dt.value));

  const handleDownload = async (doc) => {
    try {
      setDownloading(doc.id);
      const response = await api.get(`/documents/${doc.id}/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", doc.fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error(t("documents.downloadError") || "Erreur");
    } finally {
      setDownloading(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !selectedType) {
      toast.error(t("documents.selectFileAndType") || "Veuillez choisir un fichier et un type");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("doc_type", selectedType);
      await api.post(`/patients/${user.id}/documents`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(t("documents.uploadSuccess") || "Document envoyé");
      setShowUpload(false);
      setSelectedFile(null);
      setSelectedType("");
      fetchDocs();
    } catch {
      toast.error(t("documents.uploadError") || "Erreur");
    } finally {
      setUploading(false);
    }
  };

  const resetUpload = () => {
    setShowUpload(false);
    setSelectedFile(null);
    setSelectedType("");
  };

  return (
    <PatientLayout>
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 animate-fade-in-up">
          <div className="min-w-0">
            <h1 className="text-2xl font-extrabold text-slate-900 tracking-normal" style={{ fontFamily: "'Work Sans', 'Nunito', sans-serif" }} data-testid="patient-documents-title">
              {language === "ar" ? "ملفي" : "Mon dossier"}
            </h1>
            <p className="text-slate-500 mt-1" data-testid="patient-documents-subtitle">{t("documents.subtitle")}</p>
          </div>
          <button
            onClick={() => setShowUpload(true)}
            data-testid="upload-doc-btn"
            className="w-full sm:w-auto min-h-12 flex items-center justify-center gap-2 bg-[#2C5E53] text-white px-4 py-3 rounded-2xl text-sm font-extrabold hover:bg-[#244d44] transition-colors"
          >
            <Upload className="w-4 h-4" />
            {t("documents.uploadDocument")}
          </button>
        </div>

        {/* Upload Modal */}
        {showUpload && (
          <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-3 sm:p-4" onClick={resetUpload}>
            <div
              className="bg-white w-full sm:max-w-md rounded-2xl p-5 space-y-4 animate-fade-in-up shadow-2xl border border-white max-h-[86vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
              data-testid="patient-upload-modal-sheet"
            >
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-slate-800" data-testid="patient-upload-modal-title">{t("documents.uploadModalTitle")}</h2>
                <button onClick={resetUpload} data-testid="close-upload-modal" className="p-2 hover:bg-slate-100 rounded-xl">
                  <X className="w-5 h-5 text-slate-400" />
                </button>
              </div>

              {/* Doc type selection */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">{t("documents.type")}</label>
                {unsureDocType && (
                  <button
                    type="button"
                    onClick={() => setSelectedType(unsureDocType.value)}
                    data-testid="select-type-unsure"
                    className={`w-full mb-3 px-4 py-3 rounded-2xl text-sm font-extrabold border-2 transition-all text-left ${
                      selectedType === unsureDocType.value
                        ? "border-[#2C5E53] bg-[#E9F0EE] text-[#2C5E53]"
                        : "border-[#2C5E53]/10 bg-[#E9F0EE]/70 text-[#2C5E53] hover:border-[#2C5E53]/30"
                    }`}
                  >
                    {t("documents.unsureType")}
                    <span className="block text-xs font-semibold text-slate-500 mt-1">{t("documents.unsureTypeHint")}</span>
                  </button>
                )}
                <div className="grid grid-cols-2 gap-2">
                  {uploadDocTypes.map(dt => (
                    <button
                      key={dt.value}
                      onClick={() => setSelectedType(dt.value)}
                      data-testid={`select-type-${dt.value}`}
                      className={`px-3 py-2.5 rounded-xl text-sm font-medium border-2 transition-all text-left ${
                        selectedType === dt.value
                          ? "border-[#2C5E53] bg-[#E9F0EE] text-[#2C5E53]"
                          : "border-slate-200 text-slate-600 hover:border-slate-300"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {selectedType === dt.value && <Check className="w-4 h-4 text-emerald-600" />}
                        <div>
                          <span>{dt.label}</span>
                          <p className="text-[10px] text-slate-400">{dt.destination}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* File selection */}
              <div>
                <label className="text-sm font-medium text-slate-700 mb-2 block">{t("documents.file")}</label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  className="hidden"
                  data-testid="file-input"
                  onChange={e => setSelectedFile(e.target.files?.[0] || null)}
                />
                {selectedFile ? (
                  <div className="flex items-center gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                    <FileText className="w-5 h-5 text-emerald-600" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-emerald-800 truncate">{selectedFile.name}</p>
                      <p className="text-xs text-emerald-600">
                        {selectedFile.size > 1024 * 1024
                          ? `${(selectedFile.size / (1024 * 1024)).toFixed(1)} Mo`
                          : `${Math.round(selectedFile.size / 1024)} Ko`}
                      </p>
                    </div>
                    <button onClick={() => { setSelectedFile(null); fileInputRef.current.value = ""; }} data-testid="remove-selected-file-btn" className="p-1 hover:bg-emerald-100 rounded-lg">
                      <X className="w-4 h-4 text-emerald-600" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="choose-file-btn"
                    className="w-full p-6 border-2 border-dashed border-slate-300 rounded-xl text-center hover:border-emerald-400 hover:bg-emerald-50/50 transition-colors"
                  >
                    <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                    <p className="text-sm font-medium text-slate-600">{t("documents.chooseFile")}</p>
                    <p className="text-xs text-slate-400 mt-1">{t("documents.fileFormats")}</p>
                  </button>
                )}
              </div>

              {/* Submit */}
              <button
                onClick={handleUpload}
                disabled={!selectedFile || !selectedType || uploading}
                data-testid="submit-upload-btn"
                className="w-full py-3 bg-emerald-600 text-white rounded-xl font-semibold text-sm hover:bg-emerald-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t("documents.sending")}
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    {t("documents.send")}
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Filter buttons */}
        <div className="flex gap-2 overflow-x-auto pb-1 animate-fade-in-up stagger-1">
          <button
            onClick={() => setFilter("ALL")}
            data-testid="filter-all"
            className={`flex-shrink-0 px-4 py-2 rounded-xl text-sm font-bold transition-colors ${filter === "ALL" ? "bg-[#2C5E53] text-white" : "bg-white border border-[#2C5E53]/10 text-slate-600"}`}
          >
            {t("documents.all") || "Tous"}
          </button>
          {safeDocTypes.map(dt => (
            <button
              key={dt.value}
              onClick={() => setFilter(dt.value)}
              data-testid={`filter-${dt.value}`}
              className={`flex-shrink-0 px-4 py-2 rounded-xl text-sm font-bold transition-colors ${filter === dt.value ? "bg-[#2C5E53] text-white" : "bg-white border border-[#2C5E53]/10 text-slate-600"}`}
            >
              {dt.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-20 bg-slate-100 rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="patient-card text-center py-9 animate-fade-in-up" data-testid="patient-documents-empty-state">
            <div className="w-16 h-16 bg-[#E9F0EE] rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm">
              <FileText className="w-8 h-8 text-[#2C5E53]" />
            </div>
            <p className="text-slate-800 font-extrabold text-lg" data-testid="patient-documents-empty-title">{t("documents.noDocumentsTitle")}</p>
            <p className="text-slate-500 mt-2 max-w-xs mx-auto leading-relaxed" data-testid="patient-documents-empty-hint">{t("documents.noDocumentsHint")}</p>
            <button
              onClick={() => setShowUpload(true)}
              data-testid="empty-upload-btn"
              className="mt-5 px-5 py-3 bg-[#2C5E53] text-white rounded-2xl text-sm font-extrabold hover:bg-[#244d44] transition-colors inline-flex items-center gap-2"
            >
              <Upload className="w-4 h-4" />
              {t("documents.uploadFirst") || "Envoyer votre premier document"}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((doc) => {
              const docColor = DOC_TYPE_COLORS[doc.type] || "bg-slate-100 text-slate-700";
              const docLabel = doc.typeLabel || doc.type;
              return (
                <div
                  key={doc.id}
                  data-testid={`doc-${doc.id}`}
                  className="patient-card flex items-start gap-3 animate-fade-in-up"
                >
                  <div className="w-11 h-11 rounded-2xl bg-[#E9F0EE] flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-[#2C5E53]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 truncate text-base">{doc.fileName}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${docColor}`}>
                        {docLabel}
                      </span>
                      <span className="text-xs text-slate-400">
                        {new Date(doc.createdAt).toLocaleDateString(language === "ar" ? "ar-MA" : "fr-FR")}
                      </span>
                      {doc.fileSize && (
                        <span className="text-xs text-slate-400">
                          {doc.fileSize > 1024 * 1024
                            ? `${(doc.fileSize / (1024 * 1024)).toFixed(1)} Mo`
                            : `${Math.round(doc.fileSize / 1024)} Ko`}
                        </span>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] font-bold text-slate-500">
                      <span className="rounded-full bg-[#F3F1EC] px-2 py-1" data-testid={`doc-${doc.id}-source`}>
                        {getDocumentSourceLabel(doc, t)}
                      </span>
                      <span className="rounded-full bg-[#E9F0EE] px-2 py-1 text-[#2C5E53]" data-testid={`doc-${doc.id}-status`}>
                        {getDocumentStatusLabel(doc, t)}
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDownload(doc)}
                    disabled={downloading === doc.id}
                    data-testid={`download-doc-${doc.id}`}
                    className="p-2.5 bg-[#E9F0EE] text-[#2C5E53] rounded-xl hover:bg-[#dfe9e5] transition-colors flex-shrink-0 disabled:opacity-50"
                  >
                    <Download className={`w-5 h-5 ${downloading === doc.id ? "animate-pulse" : ""}`} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </PatientLayout>
  );
}
